import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, Profile, Subscription } from '../supabaseClient';
import { User, AuthError } from '@supabase/supabase-js';

function isAbortLike(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as any;
  return (
    e.name === 'AbortError' ||
    (typeof e.message === 'string' && e.message.toLowerCase().includes('abort')) ||
    (typeof e.details === 'string' && e.details.toLowerCase().includes('abort'))
  );
}

type SignUpResult = { error: AuthError | null; needsConfirmation: boolean };

type AuthContextType = {
  user: User | null;
  profile: Profile | null;
  subscription: Subscription | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string, invitationToken?: string) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  isPro: boolean;
  refreshSubscription: () => Promise<void>;
};

const INVITE_ERROR_MESSAGE =
  'WatchWicket is currently in private beta. A valid invitation is required to create an account.';

function isInvitationEnforcementError(error: AuthError | null): boolean {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  return msg.includes('invitation is by invitation only') ||
    msg.includes('registration is by invitation only');
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        if (isAbortLike(error)) return;
        console.warn('[Auth] Profile fetch failed (non-blocking):', error.message);
        return;
      }

      if (data) {
        setProfile(data);
      } else {
        await ensureProfileExists(userId);
      }
    } catch (err: any) {
      if (isAbortLike(err)) return;
      console.warn('[Auth] Profile fetch exception (non-blocking):', err);
    }
  };

  const ensureProfileExists = async (userId: string) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) return;

      const { data, error } = await supabase
        .from('profiles')
        .upsert({
          id: userId,
          email: user.data.user.email || '',
          full_name: user.data.user.user_metadata?.full_name || user.data.user.user_metadata?.name || '',
          avatar_url: user.data.user.user_metadata?.avatar_url || null,
          google_id: user.data.user.user_metadata?.provider_id || null,
        }, { onConflict: 'id' })
        .select()
        .maybeSingle();

      if (error) {
        console.warn('[Auth] Profile creation failed (non-blocking):', error.message);
      } else if (data) {
        setProfile(data);
      }
    } catch (err: any) {
      if (isAbortLike(err)) return;
      console.warn('[Auth] Profile creation exception (non-blocking):', err);
    }
  };

  const fetchSubscription = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (!error && data) {
        setSubscription(data);
      }
    } catch (err: any) {
      if (isAbortLike(err)) return;
      console.warn('[Auth] Subscription fetch exception (non-blocking):', err);
    }
  };

  const refreshSubscription = async () => {
    if (user) {
      await fetchSubscription(user.id);
    }
  };

  useEffect(() => {
    // Do NOT call supabase.auth.getSession() on mount.
    //
    // getSession() acquires the SDK's internal navigator lock, which races
    // against detectSessionInUrl's own lock acquisition in the constructor.
    // When the lock times out (10s), the SDK throws "signal is aborted without
    // reason". Instead, rely solely on onAuthStateChange, which fires
    // INITIAL_SESSION (from storage) and SIGNED_IN (from detectSessionInUrl)
    // without competing for the lock.

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const isOnCallback = window.location.pathname === '/auth/callback';

      (async () => {
        try {
          setUser(session?.user ?? null);
          if (session?.user && !isOnCallback) {
            await fetchProfile(session.user.id);
            await fetchSubscription(session.user.id);
          } else if (!session?.user) {
            setProfile(null);
            setSubscription(null);
          }
          setLoading(false);
        } catch (err: any) {
          if (isAbortLike(err)) return;
          console.error('[Auth] Error in auth state change:', err);
          setLoading(false);
        }
      })();
    });

    return () => {
      authSubscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName?: string, invitationToken?: string) => {
    const redirectTo = `${window.location.origin}/auth/callback`;

    const trimmedToken = (invitationToken || '').trim();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName || '',
          invitation_token: trimmedToken || undefined,
        },
        emailRedirectTo: redirectTo,
      },
    });

    if (error) {
      console.error('[Auth] Sign up error:', error.message);
      if (isInvitationEnforcementError(error)) {
        return {
          error: { ...error, message: INVITE_ERROR_MESSAGE } as AuthError,
          needsConfirmation: false,
        };
      }
      return { error, needsConfirmation: false };
    }

    const needsConfirmation = !data.session;

    if (needsConfirmation) {
      console.warn('[Auth] Sign up succeeded but email confirmation is required');
    } else {
      console.log('[Auth] Sign up successful, session created');
    }

    return { error: null, needsConfirmation };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      console.error('[Auth] Sign in failed:', error.message);
    } else {
      console.log('[Auth] Sign in successful:', data.user?.email);
    }

    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSubscription(null);
  };

  const resetPassword = async (email: string) => {
    const redirectTo = `${window.location.origin}/auth/callback?type=recovery`;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectTo,
    });

    if (error) {
      console.error('[Auth] Reset password error:', error.message);
    } else {
      console.log('[Auth] Reset password email sent');
    }

    return { error };
  };

  const signInWithGoogle = async () => {
    const redirectTo = `${window.location.origin}/auth/callback`;

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo,
      },
    });

    if (error) {
      console.error('[Auth] Google sign in failed:', error.message);
    } else {
      console.log('[Auth] Google sign in initiated');
    }

    return { error };
  };

  const urlParams = new URLSearchParams(window.location.search);
  const hasProParam = urlParams.get('pro') === '1';
  const localProOverride = localStorage.getItem('watchwicket_pro_override') === 'true';

  useEffect(() => {
    if (hasProParam) {
      localStorage.setItem('watchwicket_pro_override', 'true');
    }
  }, [hasProParam]);

  const isPro =
    subscription?.subscription_type === 'pro' && subscription?.status === 'active' ||
    localProOverride ||
    hasProParam;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        subscription,
        loading,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        resetPassword,
        isPro,
        refreshSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
